/**
 * ZuperBooks Environment Configuration
 * 
 * RUNTIME environment detection based on hostname
 * No build-time env variables needed - same build works everywhere!
 * 
 * Architecture:
 * - Production: zuperbooks.com → zuperbooks.com/api
 * - Preview: *.preview.emergentagent.com → same domain/api
 * - Development: localhost → localhost:8001
 */

/**
 * Detect environment based on current hostname
 */
const detectEnvironment = () => {
  const hostname = window.location.hostname;
  
  // Production domains
  if (hostname === 'zuperbooks.com' || hostname === 'www.zuperbooks.com' || hostname === 'zupertools.com' || hostname === 'www.zupertools.com') {
    return 'production';
  }
  
  // Preview/Emergent domains
  if (hostname.includes('.preview.emergentagent.com') || 
      hostname.includes('.emergent.host') ||
      hostname.includes('.emergentagent.com')) {
    return 'preview';
  }
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  
  // Unknown - default to production behavior
  console.warn('⚠️ Unknown hostname, defaulting to production mode:', hostname);
  return 'production';
};

/**
 * Get backend URL based on detected environment
 * Uses SAME-DOMAIN architecture for production
 */
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const environment = detectEnvironment();
  
  // Environment-specific backend URLs
  // IMPORTANT: For production, frontend and backend are on the SAME domain
  // The Emergent deployment serves both from the same URL
  const backendMap = {
    production: {
      // Production: Same domain (Emergent serves frontend + backend together)
      'zuperbooks.com': 'https://zuperbooks.com',
      'www.zuperbooks.com': 'https://zuperbooks.com',
      'zupertools.com': 'https://zupertools.com',
      'www.zupertools.com': 'https://zupertools.com',
    },
    preview: {
      // Preview: Same domain as frontend (Emergent handles routing)
      'default': `${protocol}//${hostname}`
    },
    development: {
      // Development: Local backend server on different port
      'localhost': 'http://localhost:8001',
      '127.0.0.1': 'http://localhost:8001',
    }
  };
  
  // Get URL from map
  let backendUrl;
  
  if (environment === 'production') {
    backendUrl = backendMap.production[hostname] || `${protocol}//${hostname}`;
  } else if (environment === 'preview') {
    backendUrl = backendMap.preview['default'];
  } else if (environment === 'development') {
    backendUrl = backendMap.development[hostname] || 'http://localhost:8001';
  }
  
  // Allow override via env variable (build-time)
  if (process.env.REACT_APP_BACKEND_URL) {
    console.log('🔧 Backend URL overridden by REACT_APP_BACKEND_URL');
    backendUrl = process.env.REACT_APP_BACKEND_URL;
  }
  
  return backendUrl.replace(/\/$/, ''); // Remove trailing slash
};

/**
 * Environment detection and configuration
 */
const environment = detectEnvironment();
const backendUrl = getBackendUrl();

/**
 * Configuration object - exported for use throughout app
 */
export const config = {
  // Environment
  environment: environment,
  
  // Backend URL
  backendUrl: backendUrl,
  API_URL: backendUrl, // Alias for consistency
  
  // API prefix (can be /api or /api/v1)
  apiPrefix: '/api/v1',
  
  // Full API base URL
  apiBaseUrl: `${backendUrl}/api/v1`,
  
  // Auth endpoints
  authUrl: `${backendUrl}/api/v1/auth`,
  
  // Frontend URL (for OAuth redirects)
  frontendUrl: window.location.origin,
  
  // Environment flags
  isDevelopment: environment === 'development',
  isPreview: environment === 'preview',
  isProduction: environment === 'production',
  
  // Debug mode
  debug: true, // Always enable for troubleshooting
};

/**
 * Validate and log environment configuration
 * Call this on app startup to verify correct detection
 */
export const validateEnvironmentConfig = () => {
  console.group('🌍 ZUPERBOOKS ENVIRONMENT CONFIGURATION');
  console.log('=' .repeat(60));
  
  console.log('📍 DETECTED ENVIRONMENT:', {
    environment: config.environment.toUpperCase(),
    hostname: window.location.hostname,
    protocol: window.location.protocol,
  });
  
  console.log('🔗 API CONFIGURATION:', {
    backendUrl: config.backendUrl,
    apiBaseUrl: config.apiBaseUrl,
    authUrl: config.authUrl,
  });
  
  console.log('🎯 FRONTEND:', {
    url: config.frontendUrl,
    hostname: window.location.hostname,
  });
  
  console.log('⚙️ FLAGS:', {
    isDevelopment: config.isDevelopment,
    isPreview: config.isPreview,
    isProduction: config.isProduction,
  });
  
  // Validation
  try {
    new URL(config.backendUrl);
    console.log('✅ Backend URL is valid');
  } catch (error) {
    console.error('❌ Backend URL is INVALID:', config.backendUrl);
  }
  
  console.log('=' .repeat(60));
  console.groupEnd();
  
  return true;
};

/**
 * Get full API URL for an endpoint
 * @param {string} endpoint - API endpoint path (e.g., '/settings/integrations')
 * @returns {string} Full URL
 */
export const getApiUrl = (endpoint) => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  // If endpoint already includes /api, use backendUrl directly
  if (cleanEndpoint.startsWith('api')) {
    return `${config.backendUrl}/${cleanEndpoint}`;
  }
  
  // Otherwise, prepend /api/v1
  return `${config.apiBaseUrl}/${cleanEndpoint}`;
};

/**
 * Debug helper - exposes config to browser console
 * Usage: Type 'config' in browser console
 */
if (typeof window !== 'undefined') {
  // Make config globally accessible
  window.config = config;
  
  // Debug function
  window.debugZuperBooksConfig = () => {
    validateEnvironmentConfig();
  };
  
  console.log('💡 Debug: Type "config" or "config.environment" in console to see configuration');
}

export default config;
